/**
 * The queue itself: a semantic table at ≥768px inside its own horizontal-scroll
 * container, stacked labeled cards below.
 *
 * That is the v2 table convention this repo already uses (`ChargeHistory`,
 * `HoursTab`, `AmendmentsTab`), and it is what this screen needs rather than
 * `DataGridTable` — which is a `@ts-nocheck` MUI X v5 wrapper whose only two
 * usage sites are in the dead `dashboardv2/` tree.
 *
 * Engagement hydration is a DETAIL PANEL, never a column set. Most rows here are
 * consultations, and giving four engagement-only columns permanent width would
 * make the common row mostly empty. Note that a null `engagementId` does not
 * mean "not an engagement" — a manually recorded engagement payment carries no
 * metadata — so the panel keys on `sourceType`, which is authoritative.
 */
import { useState } from 'react';

import { ExpertPaymentSourceType } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';

import StatusChip from '../StatusChip';
import { formatAge, formatDateTime, formatMoney } from '../format';
import { KIND_LABEL, RAIL_LABEL, SOURCE_LABEL } from '../vocabulary';
import RowActions, { type PendingAction, SettlementDialog } from './RowActions';
import type { QueuePayment } from './types';

const COLUMNS = ['Payee', 'Amount', 'For', 'Rail', 'Age', 'Status', ''] as const;

interface PaymentTableProps {
  payments: QueuePayment[];
  onSettled: () => void;
  executionFrozen: boolean;
  onExecutionFrozen: () => void;
}

export default function PaymentTable({
  payments,
  onSettled,
  executionFrozen,
  onExecutionFrozen,
}: PaymentTableProps): JSX.Element {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Held here, not per row: the desktop table and the mobile card list both
  // render, so per-row dialog state would exist twice for every row.
  const [action, setAction] = useState<PendingAction | null>(null);

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-surface-border">
              {COLUMNS.map((h, i) => (
                <th
                  key={h || `actions-${i}`}
                  className="px-12 py-8 text-on-surface-muted subtitle-2"
                  scope="col"
                >
                  {h || <span className="sr-only">Actions</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-surface-border align-top">
                <td className="px-12 py-12 text-on-surface body-2">
                  <Payee payment={p} />
                </td>
                <td className="px-12 py-12 text-on-surface body-2">
                  <Amount payment={p} />
                </td>
                <td className="px-12 py-12 text-on-surface body-2">
                  <For payment={p} expanded={expandedId === p.id} onToggle={setExpandedId} />
                </td>
                <td className="px-12 py-12 text-on-surface body-2">
                  {RAIL_LABEL[p.rail] ?? p.rail}
                </td>
                <td className="px-12 py-12 text-on-surface body-2">{formatAge(p.createdAt)}</td>
                <td className="px-12 py-12 text-on-surface body-2">
                  <StatusChip status={p.status} failureReason={p.failureReason} />
                </td>
                <td className="px-12 py-12 text-on-surface body-2">
                  <RowActions payment={p} onAction={setAction} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="flex flex-col gap-16 md:hidden">
        {payments.map((p) => (
          <li
            key={p.id}
            className="flex flex-col gap-12 rounded-md border border-surface-border bg-surface-elevated p-16"
          >
            <Cell label="Payee">
              <Payee payment={p} />
            </Cell>
            <Cell label="Amount">
              <Amount payment={p} />
            </Cell>
            <Cell label="For">
              <For payment={p} expanded={expandedId === p.id} onToggle={setExpandedId} />
            </Cell>
            <Cell label="Rail">{RAIL_LABEL[p.rail] ?? p.rail}</Cell>
            <Cell label="Age">{formatAge(p.createdAt)}</Cell>
            <Cell label="Status">
              <StatusChip status={p.status} failureReason={p.failureReason} />
            </Cell>
            <RowActions payment={p} onAction={setAction} />
          </li>
        ))}
      </ul>

      <SettlementDialog
        action={action}
        onDismiss={() => setAction(null)}
        onSettled={onSettled}
        executionFrozen={executionFrozen}
        onExecutionFrozen={onExecutionFrozen}
      />
    </>
  );
}

function Cell({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-on-surface-muted subtitle-2">{label}</span>
      <span className="text-on-surface body-2">{children}</span>
    </div>
  );
}

/**
 * A name where one resolves, the id always.
 *
 * The id is not decoration: it is what the api keys on, what a support thread
 * quotes, and the disambiguator when two experts share a name.
 */
function Payee({ payment }: { payment: QueuePayment }): JSX.Element {
  return (
    <span className="flex flex-col gap-2">
      <span>{payment.payeeIdentity?.name ?? 'Unnamed expert'}</span>
      <span className="text-on-surface-muted body-2">{payment.payee}</span>
    </span>
  );
}

function Amount({ payment }: { payment: QueuePayment }): JSX.Element {
  const kind = KIND_LABEL[payment.kind];
  return (
    <span className="flex flex-col gap-2">
      <span>{formatMoney(payment.amount)}</span>
      {kind && payment.kind !== 'work_fee' ? (
        <span className="text-on-surface-muted body-2">{kind}</span>
      ) : null}
    </span>
  );
}

function For({
  payment,
  expanded,
  onToggle,
}: {
  payment: QueuePayment;
  expanded: boolean;
  onToggle: (id: string | null) => void;
}): JSX.Element {
  const isEngagement = payment.sourceType === ExpertPaymentSourceType.EngagementHoursSubmission;

  return (
    <span className="flex flex-col gap-2">
      <span>{SOURCE_LABEL[payment.sourceType] ?? payment.sourceType}</span>
      <span className="text-on-surface-muted body-2">{payment.description}</span>
      <Button
        variant="tertiary"
        size="small"
        className="w-fit px-[0]"
        onClick={() => onToggle(expanded ? null : payment.id)}
      >
        {expanded ? 'Hide details' : 'Details'}
      </Button>
      {expanded ? <Details payment={payment} isEngagement={isEngagement} /> : null}
    </span>
  );
}

function Details({
  payment,
  isEngagement,
}: {
  payment: QueuePayment;
  isEngagement: boolean;
}): JSX.Element {
  return (
    <dl className="mt-8 flex flex-col gap-8 rounded-md border border-surface-border bg-surface-card p-12">
      <Detail label="Source id">{payment.sourceId}</Detail>
      <Detail label="Created">{formatDateTime(payment.createdAt)}</Detail>
      <Detail label="Created by">{payment.createdBy}</Detail>
      {payment.executedBy ? (
        // NOT labelled "executed by" unconditionally: on a canceled row this
        // field holds the CANCELLER, and calling them the executor of a payment
        // that never happened is a lie the audit trail cannot afford.
        <Detail label={payment.status === 'canceled' ? 'Canceled by' : 'Actioned by'}>
          {payment.executedBy}
        </Detail>
      ) : null}
      {payment.attemptCount > 0 ? (
        <Detail label="Attempts">{String(payment.attemptCount)}</Detail>
      ) : null}
      {payment.providerRef ? <Detail label="Reference">{payment.providerRef}</Detail> : null}
      {payment.paidAt ? <Detail label="Paid">{formatDateTime(payment.paidAt)}</Detail> : null}
      {isEngagement ? (
        <>
          <Detail label="Engagement">{payment.engagementId ?? 'Not recorded on this row'}</Detail>
          <Detail label="Hours submission">
            {payment.hoursSubmissionId ?? 'Not recorded on this row'}
          </Detail>
          {payment.hoursConfirmed ? (
            <Detail label="Hours confirmed">{payment.hoursConfirmed}</Detail>
          ) : null}
          {payment.periodStart && payment.periodEnd ? (
            // Labelled as the SUBMISSION's period: the api's own field
            // description warns it will not line up with the engagement's
            // billing period, and calling it "billing period" here would
            // invite exactly that mistake.
            <Detail label="Hours submission period">
              {`${payment.periodStart} – ${payment.periodEnd}`}
            </Detail>
          ) : null}
        </>
      ) : null}
      {payment.reasoning ? (
        // Append-only trail, rendered as history. Never a prefilled input.
        <Detail label="Operator notes">
          <span className="whitespace-pre-wrap">{payment.reasoning}</span>
        </Detail>
      ) : null}
    </dl>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <dt className="text-on-surface-muted util-caption">{label}</dt>
      <dd className="text-on-surface body-2">{children}</dd>
    </div>
  );
}
